/**
 * 認証セレモニーの手続き（ARCHITECTURE.md §7.1）。
 *
 * 流れは 2 段階:
 *   1. `POST /auth/portal/login` → 2FA 不要なら accessToken、必要なら mfaTicket
 *   2. `POST /auth/portal/login/mfa` → accessToken
 *
 * access token はメモリのみ（`lib/api.ts`）。リロード時は refresh Cookie から
 * `bootstrapSession()` で復帰する。
 */
import {
  ApiError,
  authApi,
  publicApi,
  refreshSession,
  setAccessToken,
  setSessionLostHandler,
} from './api.js';
import type {
  CountedList,
  LoginResponse,
  LoginSuccess,
  PortalUser,
  PublicConfig,
  SessionInfo,
} from './api-types.js';
import { authStore } from '../stores/auth.js';

/* -------------------------------------------------------------- 起動時の復帰 */

let bootstrapPromise: Promise<void> | null = null;

/**
 * refresh Cookie から access token を復帰し、`/me` で本人情報を取る。
 * ルーターの `beforeLoad` から複数回呼ばれるので、1 回だけ走らせて共有する。
 */
export function bootstrapSession(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = (async () => {
    const ok = await refreshSession();
    if (!ok) {
      authStore.setAnonymous();
      return;
    }
    try {
      const me = await authApi.get<PortalUser>('/me', { noRetry: true });
      authStore.setUser(me);
    } catch {
      // refresh は通ったのに /me が落ちる = セッションは死んでいる扱いにする。
      setAccessToken(null);
      authStore.setAnonymous();
    }
  })();

  return bootstrapPromise;
}

/** ログアウト後など、次回の `bootstrapSession()` を再実行させたいときに呼ぶ。 */
function resetBootstrap(): void {
  bootstrapPromise = null;
}

/** 401 → refresh も失敗、の局面で全画面をログイン画面へ落とすためのハンドラ。 */
setSessionLostHandler(() => {
  resetBootstrap();
  authStore.setAnonymous();
});

/* ---------------------------------------------------------------- ログイン */

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await authApi.post<LoginResponse>(
    '/login',
    { email, password },
    { anonymous: true, noRetry: true },
  );
  await applyIfSuccess(res);
  return res;
}

export async function loginMfa(
  mfaTicket: string,
  method: 'totp' | 'recovery',
  code: string,
): Promise<LoginSuccess> {
  const res = await authApi.post<LoginSuccess>(
    '/login/mfa',
    { mfaTicket, method, code },
    { anonymous: true, noRetry: true },
  );
  await applyIfSuccess(res);
  return res;
}

/**
 * 開発者ワンクリックログイン（dev 限定）。
 * サーバー側でも `MAILSV_ENV` と `DEV_LOGIN_ENABLED` を検査しており、
 * 本番では 404 を返す。ここでの呼び出し可否は `publicConfig.devLogin` との AND。
 */
export async function devLogin(): Promise<LoginSuccess> {
  const res = await authApi.post<LoginSuccess>(
    '/dev-login',
    {},
    { anonymous: true, noRetry: true },
  );
  await applyIfSuccess(res);
  return res;
}

/**
 * ログイン応答からセッションを確立する。
 *
 * ログイン応答の `user` は id / email / displayName / role / organizationId しか
 * 持たない。2 段階認証の状態（`mfaEnabled`）や所属組織の一覧は `/me` にしか無く、
 * それらを読む画面（セキュリティ・設定）が undefined を掴まないよう、
 * ここで必ず `/me` を取り直して完全な形をストアに入れる。
 */
async function applyIfSuccess(res: LoginResponse): Promise<void> {
  if (!('accessToken' in res) || typeof res.accessToken !== 'string') return;
  setAccessToken(res.accessToken);
  try {
    authStore.setUser(await fetchMe());
  } catch {
    // `/me` が落ちてもログイン自体は成立している。足りない項目は
    // 既定値で埋め、次の `bootstrapSession()` で取り直す。
    authStore.setUser({
      ...res.user,
      permissions: res.user.permissions ?? [],
      mfaEnabled: res.user.mfaEnabled ?? false,
      recoveryCodesRemaining: res.user.recoveryCodesRemaining ?? 0,
      mustChangePassword: res.user.mustChangePassword ?? false,
      amr: res.user.amr ?? [],
      organizations: res.user.organizations ?? [],
    });
  }
  // ログイン成功後は「復帰済み」とみなす。
  bootstrapPromise = Promise.resolve();
}

export async function logout(): Promise<void> {
  try {
    await authApi.post<void>('/logout', undefined, { noRetry: true });
  } catch {
    // サーバー側で既に失効していても、クライアントの状態は必ず落とす。
  }
  setAccessToken(null);
  resetBootstrap();
  authStore.setAnonymous();
}

/* ------------------------------------------------------------------ その他 */

export function fetchMe(): Promise<PortalUser> {
  return authApi.get<PortalUser>('/me');
}

/**
 * ログイン中のセッション一覧。
 *
 * API は `{items,total}` で返す（配列ではない）。以前ここが配列前提だったため、
 * 画面側の `sessions.data.map(...)` が実行時に落ちていた。
 */
export async function fetchSessions(): Promise<SessionInfo[]> {
  const res = await authApi.get<CountedList<SessionInfo>>('/sessions');
  return res.items;
}

export function revokeSession(id: string): Promise<{ ok: boolean }> {
  return authApi.del<{ ok: boolean }>(`/sessions/${encodeURIComponent(id)}`);
}

export function revokeOtherSessions(): Promise<{ revoked: number }> {
  return authApi.post<{ revoked: number }>('/sessions/revoke-others');
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return authApi.post<void>('/password/change', { currentPassword, newPassword });
}

export function forgotPassword(email: string): Promise<void> {
  return authApi.post<void>('/password/forgot', { email }, { anonymous: true, noRetry: true });
}

/**
 * 再設定メールのリンク先（`/reset-password?token=...`）から呼ぶ。
 *
 * **`anonymous: true` が必須。** ここはログインしていない訪問者が踏む画面で、
 * 付け忘れると 401 → silent refresh → 「セッションが切れました」の分岐に落ち、
 * 正しいトークンを持っていてもパスワードを設定できない。
 * 成功しても access token は発行されない（サーバは全セッションを切る）ので、
 * 画面は必ずログインへ誘導する。
 */
export function resetPassword(token: string, newPassword: string): Promise<{ ok: boolean }> {
  return authApi.post<{ ok: boolean }>(
    '/password/reset',
    { token, newPassword },
    { anonymous: true, noRetry: true },
  );
}

/**
 * 招待の受諾（`/invite?token=...`）。
 *
 * 招待されたメンバーは `org_users.status='invited'` のままではログインできない。
 * サーバ側の受け口は `POST /auth/portal/invite/accept`（**API 担当の実装待ち**）で、
 * まだ配備されていない環境では 404 が返る。その場合に画面を行き止まりに
 * しないよう、同じ「トークン + 新パスワード」で処理できる
 * `POST /auth/portal/password/reset` へ落とす。
 * どちらも「メールで受け取った単回トークンの持ち主にパスワードを設定させる」
 * 手続きなので、フォールバックしても意味は変わらない。
 */
export async function acceptInvite(token: string, newPassword: string): Promise<{ ok: boolean }> {
  try {
    return await authApi.post<{ ok: boolean }>(
      '/invite/accept',
      { token, newPassword },
      { anonymous: true, noRetry: true },
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return resetPassword(token, newPassword);
    }
    throw error;
  }
}

/* ------------------------------------------------ 自分のプロフィール（A219） */

/**
 * 自分の表示名を直す（`PATCH /auth/portal/me`）。
 *
 * **招待時に管理者が打った表示名を本人が直す唯一の経路。** API は先に
 * 在ったのに呼ぶ画面が無く、誤字がメンバー一覧・監査ログ・サポート起票に
 * 残り続けていた。成功したらストアも更新する（更新しないと、保存できた
 * のにヘッダのユーザー名が古いままになる）。
 *
 * **代理ログイン中はサーバが 403 を返す**（`principal.impersonating`）。
 * 画面側でフォームを無効にしておくこと。
 */
export async function updateProfile(displayName: string): Promise<void> {
  await authApi.patch<{ ok: boolean; displayName: string }>('/me', { displayName });
  authStore.patchUser({ displayName });
}

/**
 * ログインメールアドレスの変更を申請する（`POST /auth/portal/me/email`）。
 *
 * サーバは **202** を返し、新アドレスへ確認リンク（`/confirm-email?token=...`）を
 * 送る。**使用中のアドレスでも同じ 202 が返る**（確認メールだけ送らない）ので、
 * 画面は「送信しました」以上のことを言えない。ここで「使われています」と
 * 出せる情報は無い、というのがサーバ側の意図的な設計。
 */
export function requestEmailChange(
  newEmail: string,
  currentPassword: string,
): Promise<{ accepted: boolean; pendingEmail: string }> {
  return authApi.post<{ accepted: boolean; pendingEmail: string }>('/me/email', {
    newEmail,
    currentPassword,
  });
}

/**
 * ログインメールアドレスの変更を確定する（`POST /auth/portal/me/email/confirm`）。
 *
 * **`anonymous: true` が必須。** `/reset-password` と同じで、別端末で
 * メールを開いた人が踏む画面なので、ログインしていないのが普通の状態。
 * 付け忘れると 401 → silent refresh →「セッションが切れました」に落ちて、
 * 正しいリンクでも確定できない。
 *
 * サーバは確定と同時に**全セッションを切る**ので、成功後はログイン画面へ返す。
 */
export function confirmEmailChange(
  token: string,
): Promise<{ ok: boolean; email: string; revokedSessions: number }> {
  return authApi.post<{ ok: boolean; email: string; revokedSessions: number }>(
    '/me/email/confirm',
    { token },
    { anonymous: true, noRetry: true },
  );
}

/**
 * 所属組織の切り替え。
 *
 * **リクエストキーは `organizationId`。** `orgId` で送っていた頃は zod が
 * 400 を返し、複数組織に所属する担当者は切り替えができなかった
 * （`apps/api/src/routes/auth/portal.ts` の `/switch-org`）。
 * 応答の access token はその場で差し替える。差し替えないと、以降の
 * リクエストが古い組織のトークンで飛び続ける。
 */
export async function switchOrg(organizationId: string): Promise<void> {
  const res = await authApi.post<{ accessToken: string }>('/switch-org', { organizationId });
  setAccessToken(res.accessToken);
  authStore.setUser(await fetchMe());
}

/**
 * 再認証（fresh-auth の取り直し）。
 *
 * 決済手段の登録・即時解約のような `requireFreshAuth()` 付きの操作は、
 * ログインから 5 分を過ぎると `REAUTH_REQUIRED` になる。**再取得の経路が
 * 画面に無いと、その操作は二度と実行できない**（ログインし直すしかない）。
 *
 * **サーバは呼び出し前の access token をその場で失効させる**ので、
 * 応答の `accessToken` へ必ず差し替える。差し替えないと以降すべて 401。
 */
export async function reauth(
  method: 'totp' | 'recovery',
  code: string,
): Promise<{ freshUntil: string }> {
  const res = await authApi.post<{ accessToken: string; expiresIn: number; freshUntil: string }>(
    '/reauth',
    { method, code },
    { noRetry: true },
  );
  setAccessToken(res.accessToken);
  return { freshUntil: res.freshUntil };
}

export interface TotpSetup {
  secret: string;
  /** `otpauth://totp/...`。手入力や別端末への引き継ぎ用。 */
  uri: string;
  /**
   * QR コードの **PNG の data URI**。**キー名を API に合わせること**
   * （API は `{ secret, uri, qrDataUrl }` を返す）。以前は `otpauthUrl` /
   * `qrSvg` で食い違っており、QR が出ないまま登録できなかった。
   */
  qrDataUrl: string;
}

export function setupTotp(): Promise<TotpSetup> {
  return authApi.post<TotpSetup>('/totp/setup');
}

export interface TotpVerifyResult {
  enabled: boolean;
  recoveryCodes: string[];
  /**
   * **2 要素へ上がった新しいアクセストークン。**
   *
   * サーバは登録の確定と同時にセッションを差し替える
   * （`routes/auth/common.ts` の `upgradeSessionToMfa()`）。受け取ったら
   * 必ず載せ替えること。載せ替えないと手元の JWT の `amr` は `['pwd']` の
   * ままで、`aat` もログイン時刻のままなので fresh-auth が通らない
   * （＝登録直後に決済手段の変更などが `REAUTH_REQUIRED` で拒否される）。
   *
   * サーバ側の差し替えに失敗したときだけ欠ける。
   */
  accessToken?: string;
  expiresIn?: number;
}

/**
 * 2 段階認証の登録の確定。
 *
 * **新しいトークンを載せ替え、`/me` を引き直す。** `/me` を引き直さないと
 * ストアの `mfaEnabled` が false のままで、登録が成功しているのに画面は
 * 「未設定」を出し続ける（解除の `disableTotp()` は最初からそうしている）。
 *
 * **`/me` の失敗でこの関数を失敗させない。** 平文のリカバリコードが載るのは
 * この応答だけで、再表示はできない。`/me` が落ちたことを理由に throw すると
 * 呼び出し側の `onSuccess` が走らず、**2 段階認証は有効・リカバリコードは
 * 永久に失われた**という最悪の状態になる。
 */
export async function verifyTotp(code: string): Promise<TotpVerifyResult> {
  const res = await authApi.post<TotpVerifyResult>('/totp/verify', { code });
  if (res.accessToken) setAccessToken(res.accessToken);
  try {
    authStore.setUser(await fetchMe());
  } catch {
    // 差し替えに失敗したセッションでは 401 になりうる。リカバリコードを
    // 返しきることを優先する。
  }
  return res;
}

export function regenerateRecoveryCodes(): Promise<{ recoveryCodes: string[] }> {
  return authApi.post<{ recoveryCodes: string[] }>('/recovery-codes/regenerate');
}

/**
 * 2 段階認証の解除。
 *
 * **解除の口が画面に無いと、認証アプリを失った時点で本人でも詰む**
 * （リカバリコードも使い切っていれば、運営に頼む以外の出口が無い）。
 * サーバは fresh-auth を要求するので、`REAUTH_REQUIRED` を受けたら
 * 再認証ダイアログを通してから呼び直すこと。
 * 解除後は `/me` を取り直してストアの `mfaEnabled` を落とす。
 */
export async function disableTotp(): Promise<void> {
  await authApi.post<{ ok: boolean }>('/totp/disable', {}, { noRetry: true });
  authStore.setUser(await fetchMe());
}

/** 公開設定。dev ログインボタンの表示可否（サーバ真実）を含む。 */
export function fetchPublicConfig(): Promise<PublicConfig> {
  return publicApi.get<PublicConfig>('/config');
}

/**
 * 招待トークンが指す宛先の照会（`GET /auth/portal/invite/preview`）。
 *
 * **パスワードマネージャにユーザー名を渡すために要る。** パスワード設定画面は
 * トークンしか持たないので、これが無いとフォームに username に相当する項目が
 * 1 つも無く、1Password / Chrome は**ユーザー名を空欄で保存する**。
 * 利用者は後から手で直すことになり、直さなければ次回の自動入力が効かない。
 *
 * **失敗しても画面を止めない。** 宛先が引けないだけでパスワードは設定できる。
 * 古い API が入った環境では 404 になるので、その場合も黙って諦める。
 */
export async function previewInvite(token: string): Promise<{ email: string } | null> {
  try {
    return await authApi.get<{ email: string }>(
      `/invite/preview?token=${encodeURIComponent(token)}`,
      { anonymous: true, noRetry: true },
    );
  } catch {
    return null;
  }
}
